/**
 * Inline SVG icon set — consistent stroke-based icons (Lucide-style).
 * All icons default to 1em × 1em and inherit currentColor.
 * Pass size / color / style to override.
 */

import type { CSSProperties } from "react";

interface IconProps {
  size?: number | string;
  color?: string;
  style?: CSSProperties;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

const base = (size: number | string = "1em"): CSSProperties => ({
  width: size,
  height: size,
  display: "inline-block",
  verticalAlign: "middle",
  flexShrink: 0,
});

const svg = (
  size: number | string,
  color: string | undefined,
  style: CSSProperties | undefined,
  children: React.ReactNode,
  props: Omit<IconProps, "size" | "color" | "style">,
) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? "currentColor"}
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ ...base(size), ...style }}
    aria-hidden={props["aria-hidden"] ?? true}
  >
    {children}
  </svg>
);

export const DashboardIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </>, p);

export const AgentsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="9" cy="7" r="4" />
    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
  </>, p);

export const ProductsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </>, p);

export const TransactionsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </>, p);

export const ReportsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </>, p);

export const CashIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </>, p);

export const RequestsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </>, p);

export const BillingIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </>, p);

export const SettingsIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>, p);

export const SunIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </>, p);

export const MoonIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </>, p);

export const PowerIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </>, p);

export const MenuIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </>, p);

export const ChevronLeftIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="15 18 9 12 15 6" />
  </>, p);

export const ChevronRightIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="9 18 15 12 9 6" />
  </>, p);

export const ChevronDownIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="6 9 12 15 18 9" />
  </>, p);

export const BellIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </>, p);

export const TrendUpIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </>, p);

export const TrendDownIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </>, p);

export const UsersIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>, p);

export const ShoppingBagIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </>, p);

export const AlertTriangleIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </>, p);

export const ActivityIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </>, p);

export const ClockIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>, p);

export const BuildingIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <line x1="9" y1="22" x2="9" y2="2" />
    <line x1="15" y1="22" x2="15" y2="2" />
    <line x1="4" y1="8" x2="9" y2="8" />
    <line x1="15" y1="8" x2="20" y2="8" />
    <line x1="4" y1="14" x2="9" y2="14" />
    <line x1="15" y1="14" x2="20" y2="14" />
  </>, p);

export const XIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </>, p);

export const CheckIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <polyline points="20 6 9 17 4 12" />
  </>, p);

export const InfoIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </>, p);

export const WifiOffIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </>, p);

export const HomeIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </>, p);

export const SearchIcon = (p: IconProps) =>
  svg(p.size ?? "1em", p.color, p.style, <>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </>, p);
